/**
 * useSpeechLifecycle Hook
 * 
 * Handles speech detection lifecycle:
 * - Visual feedback on VAD detection (waveform)
 * - Message creation on server speech_started (with item_id)
 * - Speech start/stop state management
 * - Audio chunk recording for WAV blobs
 * 
 * REFACTORED (2026-01): Separated visual state from data state to fix disappearing bubbles.
 * 
 * Old flow (broken): VAD -> create placeholder with local ID -> correlate with server ID
 * New flow (fixed): VAD -> visual only -> server speech_started -> create with server ID
 * 
 * This eliminates the speechId ↔ item_id correlation that was causing bubbles to disappear.
 * Messages are ONLY created when server confirms speech with item_id.
 */

import { useRef, useCallback, useEffect } from 'react';
import { WebSocketService } from '../../services/realtime/WebSocketService';
import { WebAudioManager } from '../../services/audio';
import { logger } from '../../utils/logger';
import { VAD_ACTIVITY_LOOKBACK_MS } from '../../config/realtime';
import {
  AUDIO_UNBLOCK_DELAY_MS,
  WAV_SAMPLE_RATE,
  WAV_NUM_CHANNELS,
} from './constants';
import { createWavBlob, combineAudioChunks } from './utils';
import type { SendFunction } from './types';

// Safety timeout: if server doesn't confirm speech within this time, reset visual state
// 🔧 2025-02: Increased from 2000ms to 3500ms to match AudioRecording.SPEECH_STARTED_TIMEOUT_MS
// With tighter VAD thresholds, gate opens less on noise → give OpenAI more time to confirm
const SPEECH_CONFIRMATION_TIMEOUT_MS = 3500;


// ============================================================================
// Types
// ============================================================================

export interface UseSpeechLifecycleParams {
  send: SendFunction;
  websocketServiceRef: React.MutableRefObject<WebSocketService | null>;
  audioManagerRef: React.MutableRefObject<WebAudioManager | null>;
  audioBlockedRef: React.MutableRefObject<boolean>;
  hasReceivedFirstAudioChunkRef?: React.MutableRefObject<boolean>;
}

export interface UseSpeechLifecycleReturn {
  // Refs for audio recording
  recordedAudioChunksRef: React.MutableRefObject<Uint8Array[]>;
  isRecordingForBlobRef: React.MutableRefObject<boolean>;
  currentRecordingIdRef: React.MutableRefObject<string | null>;

  // Handlers for WebSocket events
  handleUserSpeechStarted: (openAiItemId: string) => void;
  handleUserSpeechStopped: (openAiItemId: string) => void;

  // Handler for client-side VAD
  handleSpeechDetected: () => void;

  // Handler for early visual signal (waveform appears before gate opens)
  handleSpeechLikely: () => void;

  // Handler for gate force closed (false positive cleanup)
  handleGateForceClosed: () => void;

  // Handler for the speech_stopped watchdog (silence timeout after speech_started)
  handleSpeechStoppedTimeout: () => void;

  // Reset all speech-lifecycle refs and timers. Called on input mode switch
  // to prevent the safety timeout from firing after a mode change and
  // re-triggering phantom speech state.
  resetSpeechState: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useSpeechLifecycle({
  send,
  websocketServiceRef,
  audioManagerRef,
  audioBlockedRef,
  hasReceivedFirstAudioChunkRef,
}: UseSpeechLifecycleParams): UseSpeechLifecycleReturn {
  
  // Audio recording for debugging
  const recordedAudioChunksRef = useRef<Uint8Array[]>([]);
  const isRecordingForBlobRef = useRef<boolean>(false);
  const currentRecordingIdRef = useRef<string | null>(null);
  
  // Safety timeout ref for when server doesn't confirm speech
  const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track if we've received speech_started from server
  const hasReceivedSpeechStartedRef = useRef<boolean>(false);

  // Idempotency guard for closeSpeechTurn — prevents double blob/dispatch
  const turnClosedRef = useRef(false);

  // Cleanup safety timeout on unmount
  useEffect(() => {
    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, []);

  // Handle user speech started (from OpenAI server)
  // THIS is where we create the message - with the server's item_id!
  // NOTE: In PTT mode, this is NOT called (turn_detection is null, so server
  // doesn't send speech_started events). PTT handles state transitions directly.
  const handleUserSpeechStarted = useCallback((openAiItemId: string) => {
    // Guard: skip in PTT mode (shouldn't happen, but safety net)
    if (audioManagerRef.current?.isPTTModeActive?.()) {
      logger.debug('[🎙️ PTT] Ignoring handleUserSpeechStarted in PTT mode');
      return;
    }
    // Mark that server confirmed speech
    hasReceivedSpeechStartedRef.current = true;

    // Clear safety timeout - server confirmed speech
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    
    // Ensure recording is active for this speech segment.
    // NOTE: We intentionally do NOT clear recordedAudioChunksRef here.
    // The VAD detected speech earlier and chunks have been accumulating since then.
    // Clearing them would discard the first ~300-800ms of audio that the user spoke
    // before the server sent speech_started, causing the debug playback to sound cut.
    isRecordingForBlobRef.current = true;
    
    // Mark that OpenAI recognized speech (cancels safety timeouts in audio manager)
    audioManagerRef.current?.markSpeechStarted();

    // Store server's item_id for audio blob association
    currentRecordingIdRef.current = openAiItemId;
    
    // Dispatch SERVER_SPEECH_STARTED - THIS creates the message with server's item_id
    send({ type: 'SERVER_SPEECH_STARTED', itemId: openAiItemId });
    
    // Interrupt avatar audio when user speaks
    logger.debug('[🎯 INTERRUPT] User speech detected - interrupting avatar');

    const wasAlreadyBlocked = audioBlockedRef.current;
    audioBlockedRef.current = true;
    logger.debug('[🎯 INTERRUPT] Audio blocked (was already blocked:', wasAlreadyBlocked, ')');

    // Stop playback + clear buffers
    audioManagerRef.current?.stopAllPlayback();
    audioManagerRef.current?.resetStreamingBuffer();

    // Reset first audio chunk flag so next response can be detected correctly
    if (hasReceivedFirstAudioChunkRef) {
      hasReceivedFirstAudioChunkRef.current = false;
    }

    // Unblock after short delay
    setTimeout(() => {
      logger.debug('[🎯 INTERRUPT] Unblocking audio after delay');
      audioBlockedRef.current = false;
    }, AUDIO_UNBLOCK_DELAY_MS);
    
    // Also update playback state
    send({ type: 'PLAYBACK_STOPPED' });
  }, [send, audioManagerRef, audioBlockedRef, hasReceivedFirstAudioChunkRef]);

  // Fecha o turno de fala: para a gravação do WAV, prepara o VAD para a próxima fala, cria o
  // blob e despacha SPEECH_STOPPED. Idempotente — a segunda chamada (speech_stopped atrasado
  // depois do watchdog) é um no-op.
  const closeSpeechTurn = useCallback(() => {
    if (turnClosedRef.current) return;
    turnClosedRef.current = true;

    isRecordingForBlobRef.current = false;
    logger.debug('[🔄 VAD-STATE] Processing speech_stopped:', { chunksRecorded: recordedAudioChunksRef.current.length });
    audioManagerRef.current?.prepareForNextSpeech();

    let audioBlob: Blob | undefined;
    if (recordedAudioChunksRef.current.length > 0) {
      try {
        const combined = combineAudioChunks(recordedAudioChunksRef.current);
        audioBlob = createWavBlob(combined, WAV_SAMPLE_RATE, WAV_NUM_CHANNELS);
      } catch (error) {
        console.error('[🔊 DEBUG] Failed to create audio blob:', error);
      }
    }

    send({ type: 'SPEECH_STOPPED', audioBlob });

    const targetId = currentRecordingIdRef.current;
    if (targetId && audioBlob) {
      send({ type: 'UPDATE_MESSAGE', id: targetId, updates: { audioBlob } });
    }

    currentRecordingIdRef.current = null;
    hasReceivedSpeechStartedRef.current = false;
  }, [send, audioManagerRef]);

  // Handle user speech stopped (from OpenAI server)
  // Note: openAiItemId param kept for API compatibility but not used (item_id tracked via currentRecordingIdRef)
  // NOTE: In PTT mode, this is NOT called (turn_detection is null).
  const handleUserSpeechStopped = useCallback((_openAiItemId: string) => {
    if (audioManagerRef.current?.isPTTModeActive?.()) {
      logger.debug('[🎙️ PTT] Ignoring handleUserSpeechStopped in PTT mode');
      return;
    }
    logger.debug('[🔄 VAD-STATE] OpenAI speech_stopped received - processing immediately');
    closeSpeechTurn();
  }, [closeSpeechTurn, audioManagerRef]);

  // 🛡️ O speech_stopped nunca chegou. Fechamos o turno e enviamos o áudio nós.
  const handleSpeechStoppedTimeout = useCallback(() => {
    if (audioManagerRef.current?.isPTTModeActive?.()) {
      return;
    }

    logger.warn('[🛡️ WATCHDOG] speech_stopped never arrived - closing turn and committing');

    closeSpeechTurn();
    websocketServiceRef.current?.commitAndCreateResponse();

    websocketServiceRef.current?.logDiagnostic?.('watchdog:speech_stopped_missing');
  }, [closeSpeechTurn, audioManagerRef, websocketServiceRef]);

  // Handle client-side VAD speech detection
  // Visual feedback only - NO message created here!
  // NOTE: In PTT mode, VAD is disabled so this won't fire. Guard added as safety net.
  const handleSpeechDetected = useCallback(() => {
    // Guard: skip in PTT mode (VAD is disabled, shouldn't fire)
    if (audioManagerRef.current?.isPTTModeActive?.()) {
      logger.debug('[🎙️ PTT] Ignoring handleSpeechDetected in PTT mode');
      return;
    }
    
    // Reset state for new speech segment
    hasReceivedSpeechStartedRef.current = false;
    turnClosedRef.current = false;
    
    // Start recording for WAV blob
    recordedAudioChunksRef.current = [];
    isRecordingForBlobRef.current = true;
    logger.debug('[🔊 DEBUG] Started recording for WAV blob');
    
    // Dispatch VAD_SPEECH_DETECTED - visual feedback only, no message!
    send({ type: 'VAD_SPEECH_DETECTED' });
    
    logger.debug('[🔄 SPEECH] VAD detected speech - visual feedback only, waiting for server confirmation');
    
    // Safety timeout: if server doesn't send speech_started within timeout,
    // assume it was noise and reset visual state
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
    }
    safetyTimeoutRef.current = setTimeout(() => {
      if (!hasReceivedSpeechStartedRef.current) {
        const hasRecentActivity = audioManagerRef.current?.hasRecentAudioActivity(VAD_ACTIVITY_LOOKBACK_MS);
        if (hasRecentActivity) {
          logger.debug('[🛡️ SAFETY] Skipping noise reset - recent audio activity detected');
          return;
        }
        logger.warn('[🛡️ SAFETY] Server did not confirm speech within timeout - treating as noise');
        send({ type: 'NOISE_DETECTED' });
        isRecordingForBlobRef.current = false;
        recordedAudioChunksRef.current = [];
      }
    }, SPEECH_CONFIRMATION_TIMEOUT_MS);
  }, [send]);

  // Handle early "speech likely" signal from VAD.
  // Fires ~60ms into voiced audio (or immediately on a loud/clear frame),
  // well before the gate opens. Sets visual state only — no recording, no safety timeout.
  const handleSpeechLikely = useCallback(() => {
    if (audioManagerRef.current?.isPTTModeActive?.()) {
      return;
    }
    send({ type: 'VAD_SPEECH_LIKELY' });
  }, [send, audioManagerRef]);

  // Handle gate force closed (false positive cleanup from audio manager)
  const handleGateForceClosed = useCallback(() => {
    logger.debug('[🛡️ SAFETY] Gate force closed - targeted cleanup');
    
    // Clear safety timeout
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    
    // Clear OpenAI buffer
    websocketServiceRef.current?.clearAudioBuffer();
    
    // Clear recorded audio chunks
    recordedAudioChunksRef.current = [];
    isRecordingForBlobRef.current = false;
    
    const messageId = currentRecordingIdRef.current;
    
    if (messageId) {
      // If server had already created a message, remove it
      send({ type: 'REMOVE_MESSAGE', id: messageId });
      logger.debug('[🛡️ SAFETY] Removed noise message:', messageId);
    }
    
    // Dispatch noise event to reset visual state
    send({ type: 'NOISE_DETECTED' });
    
    // Reset tracking
    currentRecordingIdRef.current = null;
    hasReceivedSpeechStartedRef.current = false;
  }, [send, websocketServiceRef]);

  // Reset all speech-lifecycle refs and timers.
  // Called on input mode switch to clear any in-flight safety timeout and
  // recording state left over from the previous mode. Without this, the
  // safety timeout (SPEECH_CONFIRMATION_TIMEOUT_MS) could fire after a mode
  // switch and dispatch NOISE_DETECTED / reset visual state at an unexpected
  // time, or isRecordingForBlobRef could stay true and corrupt the next
  // speech segment's audio blob.
  const resetSpeechState = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    isRecordingForBlobRef.current = false;
    recordedAudioChunksRef.current = [];
    currentRecordingIdRef.current = null;
    hasReceivedSpeechStartedRef.current = false;
    turnClosedRef.current = false;
  }, []);

  return {
    recordedAudioChunksRef,
    isRecordingForBlobRef,
    currentRecordingIdRef,
    handleUserSpeechStarted,
    handleUserSpeechStopped,
    handleSpeechDetected,
    handleSpeechLikely,
    handleGateForceClosed,
    handleSpeechStoppedTimeout,
    resetSpeechState,
  };
}
